import { Button, Chip, CircularProgress, Grid, MenuItem, TextField, Typography } from "@mui/material";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { getUserName } from "../../api/lensService";
import { useEffect, useMemo, useState } from "react";
import { createJiraTicket, getJiraStatus, loadJiraProjects, loadJiraStatuses, loadJiraUsers, updateJiraTicket, updateTestResult } from "../../api/jira.service";
import RichTextEditor from "../richtexteditor/richtexteditor";
import { editReport, editAlert } from "../../actions";
import {useDispatch, useSelector} from "react-redux";
import { toggleGlobalAlert,ShowGlobalErrorAlert, upsertautojiraticket } from "../../actions";
import { Alert, Snackbar } from "@mui/material"; // Import Alert and Snackbar
import * as Yup from "yup"; // Import Yup for validation

// Validation schema using Yup
const validationSchema = Yup.object().shape({
    assignee: Yup.string().required("Assignee is required"),
    project: Yup.string().required("Project is required"),
    summary: Yup.string().required("Summary is required")
});

export const JiraTicket = ({
    testResultDetails = null,
    alertDetails = null,
    project = null,
    cancel = null,
    channel = null,
    scheduled = '',
    isReportSection,
    isAutoJiraTicket
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch()
    const [users, setUsers] = useState([]);
    const [loading, isLoading] = useState(true);
    const [projects, setProjects] = useState([]);
    const [statuses, setStatus] = useState([]);
    const [description, setDescription] = useState(""); 
    const [csvFile, setCsvFile] = useState(null);


    const formik = useFormik({
        initialValues: {
            labels: '',
            tags: [],
            summary: '',
            assignee: '',
            project: '',
            status: '',
        },
        validationSchema, // Attach validation schema
        onSubmit: async (values) => {
            let body = {
                project: values?.project,
                summary: values.summary,
                issueType: "Bug",
                assignee: values.assignee,
                labels: values.tags || [],
                username: getUserName(),
                description: description, // Include the description from Textarea/RichTextEditor,
            };

            // Create a FormData object for attaching the file along with ticket details
            const formData = new FormData();
            formData.append("data", new Blob([JSON.stringify(body)], { type: "application/json" }));
            if (csvFile) {
                formData.append("file", csvFile); // your CSV file
            }           
            if (editMode) {
               const editData = {
                    ...body,
                    status: values.status,
                    isAutoJiraTicket:isAutoJiraTicket
                };
                formData.set("data", new Blob([JSON.stringify(editData)], { type: "application/json" })); 
                 // Update a Jira ticket with attachment using the FormData
                const result = await updateJiraTicket(testResultDetails?.jiraTicket, formData).catch(e => e); 
                if (["ERR_BAD_RESPONSE", "ERR_BAD_REQUEST", "fail"].includes(result?.code)) {
                   await toggleGlobalAlert(result?.response?.data?.data?.messages?.join(','))(dispatch); 
                   cancel && cancel(true);                
                    return;
                }
                if(isAutoJiraTicket){
                    const editautojirsticket = {
                        description:description
                    };
                    await upsertautojiraticket(testResultDetails?.id, editautojirsticket)(dispatch);
                }               
                await  toggleGlobalAlert("Jira Ticket Edited Successfully")(dispatch);
                cancel && cancel(true); 
                return;
            }
            
            // Create a Jira ticket with attachment using the FormData
            
            const jira = await createJiraTicket(formData, dispatch).catch(e => e); // Pass dispatch as an argument
            if (["ERR_BAD_RESPONSE", "ERR_BAD_REQUEST", "fail"].includes(jira?.response?.data?.code)) {
                toggleGlobalAlert(jira?.response?.data?.data?.messages?.join(','))(dispatch);
                cancel && cancel(true);
                return;
            }

            if (jira?.key) {
                await  toggleGlobalAlert("Jira Ticket Created Successfully")(dispatch);
                let reportData = {}
                reportData.jiraTicket = jira?.key;
                if(isReportSection){                  
                    editReport(reportData, testResultDetails?.id)(dispatch);   
                }else{
                    editAlert(reportData,alertDetails?.alertId)(dispatch);  
                }   
                window.location.reload();                
            }
            cancel && cancel(true);
        }
    });

    const handleAddLabel = (e) => {
        if (e.key === "Enter") {
            formik.setFieldValue('tags', [...(formik.values.tags || []), formik.values?.labels]);
            formik.setFieldValue('labels', '');
        }
    };

    const saveChanges = async () => {
        await formik.submitForm();
    };

    const handleDelete = (tag) => () => {
        formik.setFieldValue('tags', formik.values.tags.filter(c => c !== tag));
    };

    const editMode = useMemo(() => testResultDetails?.jiraTicket, [testResultDetails?.jiraTicket]);

    useEffect(() => {
        const loadUsers = async () => {
            const data = await loadJiraUsers();
            setUsers(data);

            const projects = await loadJiraProjects();
            setProjects(projects);

            const statuses = await loadJiraStatuses();
            setStatus(statuses);

            if (editMode) {
                const status = await getJiraStatus(testResultDetails?.jiraTicket);
                console.log(status);
                formik.setValues({
                    labels: '',
                    tags: status?.labels,
                    summary: status?.summary,
                    assignee: status?.assigneeId,
                    project: status?.project,
                    status: status?.status,
                    description: status?.description
                });
                setDescription(status?.description);
            }
            isLoading(false);
        };

        loadUsers();
    }, [editMode]);

    // Handle CSV file upload
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file && file.name.endsWith('.csv')) {
            setCsvFile(file); // Store the CSV file for upload
        } else {
            console.error('Please upload a CSV file');
        }
    };
    console.log(testResultDetails);
    return (
        <>
            {loading && <div className="data-loader"><CircularProgress /> <span>&nbsp;&nbsp;&nbsp;&nbsp;Please wait ...</span></div>}

            <div style={{ maxHeight: '500px', overflowY: 'auto', padding: '0 16px' }}>
            <Grid container spacing={3} sx={{ mt: 3, pl: 3 }}>
                <Grid item xs={4}>

                    <TextField label={ isReportSection  ? t('Report Label') : isAutoJiraTicket ? t('Alert Setting ID')  : t('Test Case Name') }
                        disabled={true} 
                        InputLabelProps={{ shrink: true }}
                        value={isReportSection ? testResultDetails?.report_label : isAutoJiraTicket ? testResultDetails?.alertSettingId : testResultDetails?.testCaseName} 
                     fullWidth />
                </Grid>
                <Grid item xs={4} sx={{ pr: 3 }}>
                <TextField label={ isReportSection  ? t('Report Type') : isAutoJiraTicket ? t('Test Case ID')  : t('Test Case ID') }
                        disabled={true} 
                        InputLabelProps={{ shrink: true }}
                        value={isReportSection ? testResultDetails?.reportTypeName : isAutoJiraTicket ? testResultDetails?.testcaseId :  testResultDetails?.testCaseId} 
                    fullWidth />
                </Grid>
                <Grid item xs={4} sx={{ pr: 3 }}>
                <TextField label={ isReportSection  ? t('Test Case Group') : isAutoJiraTicket ? t('Run IDs')  : t('Run ID') }
                       disabled={true}
                       InputLabelProps={{ shrink: true }}
                       fullWidth 
                       value={isReportSection ? testResultDetails?.test_case_group : isAutoJiraTicket ? testResultDetails?.runIds : testResultDetails?.runId} 
                     />
                </Grid>

            <Grid container spacing={2} className="bg-light-green" sx={{ alignItems: 'flex-start', padding: '8px', marginTop: '8px', marginLeft: '0px',  borderRadius: '5px', backgroundColor: 'rgba(206, 231, 230, 1)' }}>

                
            <Grid item xs={12} className="jira-details-header bg-light-green" sx={{ mt: 2 }}>
                    <Typography>{t('Jira Ticket Details')}</Typography>
                </Grid>

                        {/* Assignee Field */}
                        <Grid item xs={4}>
                            <TextField
                                label={t('Assignee *')}
                                InputLabelProps={{ shrink: true }}
                                select
                                fullWidth
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur} // Trigger validation on blur
                                value={formik.values.assignee}
                                name="assignee"
                                id="assignee"
                                error={formik.touched.assignee && Boolean(formik.errors.assignee)} // Show error if validation fails
                                helperText={formik.touched.assignee && formik.errors.assignee} // Display error message
                        
                            >
                                {users.map(user => (
                                    <MenuItem value={user.accountId} key={user.accountId}>{user.name}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* Project Field */}
                        <Grid item xs={4}>
                            <TextField
                                label={t('Project *')}
                                InputLabelProps={{ shrink: true }}
                                select
                                fullWidth
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur} // Trigger validation on blur
                                value={formik.values.project}
                                name="project"
                                id="project"
                                error={formik.touched.project && Boolean(formik.errors.project)} // Show error if validation fails
                                helperText={formik.touched.project && formik.errors.project} // Display error message
                           
                            >
                                {projects.map(project => (
                                    <MenuItem value={project.key} key={project.key}>{project.name}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* Labels Input Field */}
                            <Grid item xs={4}>
                                    <TextField
                                        label={t('Labels')}
                                        InputLabelProps={{ shrink: true }}
                                        placeholder={t('Type here and hit Enter')}
                                        fullWidth
                                        onChange={formik.handleChange}
                                        name="labels"
                                        id="labels"
                                        value={formik.values.labels}
                                        onKeyDown={handleAddLabel}
                                        sx={{
                                            backgroundColor: 'inherit', // Match the background color
                                        }}
                                    />
                                </Grid>

                                {/* Labels Display Section */}
                                <Grid item xs={12}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                                        {formik.values.tags?.map((tag) => (
                                            <Chip label={tag} onDelete={handleDelete(tag)} />
                                        ))}
                                    </div>
                                </Grid>
                    </Grid>

                <Grid item xs={12} className="bg-light-green" sx={{ pr: 3, pb: 3 }}>
                    <TextField
                        label={t('Summary *')}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        value={formik.values.summary}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur} // Trigger validation on blur
                        id="summary"
                        name="summary"
                        error={formik.touched.summary && Boolean(formik.errors.summary)} // Show error if validation fails
                        helperText={formik.touched.summary && formik.errors.summary} // Display error message
   
                    />
                </Grid>

                <Grid item xs={12} className="bg-light-green" sx={{ pr: 3, pb: 3 }}>
                    <RichTextEditor
                            value={description} // Bind the description state
                            onChange={(value) => setDescription(value)} // Update the description state on change
                    />
                    {/* <TextField
                        label={t('Description')}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        multiline
                        rows={4}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        id="description"
                        name="description"
                        variant="outlined"
                    /> */}
                </Grid>

                {/* CSV File Upload */}
                {isReportSection && ( // Conditionally render the CSV upload section
                    <Grid item xs={12} className="bg-light-green" sx={{ pr: 3, pb: 3 }}>
                        <Typography>{t('Upload Report File')}</Typography>
                        <input
                            type="file"
                            accept=".csv"
                            onChange={handleFileChange}
                            style={{ display: 'block' }}
                        />
                        {csvFile && <Typography>{csvFile.name}</Typography>} {/* Display the file name */}
                    </Grid>
                )}

                {editMode && (
                    <>
                    <Grid item xs={12} className="bg-light-green">
                        <Grid item xs={4} className="bg-light-green">
                            <TextField
                                label={t('Status')}
                                InputLabelProps={{ shrink: true }}
                                select
                                fullWidth
                                onChange={formik.handleChange}
                                value={formik.values.status}
                                name="status"
                                id="status"
                            >
                                {statuses.map(status => (
                                    <MenuItem value={status.name} key={status.name}>{status.name}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        </Grid>
                    </>
                )}

                <Grid item xs={12} className="bg-light-green" sx={{ pr: 3, pb: 3, pt: 2 }} style={{ display: 'flex', gap: '40px', justifyContent: 'center' }}>
                    <Button variant='contained' onClick={cancel}>
                        <Typography>{t('Cancel')}</Typography>
                    </Button>

                    <Button variant='contained' onClick={saveChanges}>
                        <Typography>{t(editMode ? 'Save Changes' : 'Confirm')}</Typography>
                    </Button>
                </Grid>
            </Grid>
            </div>  

        </>
    );
};
